import React, { useState, useEffect, useRef, useMemo } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { sagaActions } from '../../../../../../../sagas/sagaActions'

import { API, graphqlOperation } from 'aws-amplify';
import './DmsDashboardList.css';
import './DmsFilter.css';
import DmsListFilter from './DmsListFilter';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// import DmsTextFilter from './DmsTextFilter'; // Filter Type 2 — kept for future use
import { getDmsTrucks, _empty } from '../../../../../../../graphql/queries';
import { onRealTimeEventFailed } from '../../../../../../../graphql/subscriptions';
import { getUserSelector } from '../../../../../../../redux/reducers/reducerSlices/UserReducer';
import { getAuthReducer } from '../../../../../../../redux/reducers/reducerSlices/AuthReducer';
import { getDMSTruckAuditByControl, clearDMSTruckAuditInSaga } from '../../../../../../../sagas/DmsBucketSaga';
import { getDmsBucketSelector } from '../../../../../../../redux/reducers/reducerSlices/DmsBucketReducer';
import Popup from '../../../../../Common/Popup/Popup';
import { showAlert } from '../../../../../../Assests/Utility';
import { toast } from 'react-toastify';
import { enGB } from "date-fns/locale";


// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDateTime = (dateTime) => {
    if (!dateTime) return '-';
    return moment.utc(dateTime).format('MM/DD/YYYY HH:mm');
};

const renderDateTime = (dateTime) => {
    if (!dateTime) return '-';
    const dateStr = moment.utc(dateTime).format('MM/DD/YYYY');
    const timeStr = moment.utc(dateTime).format('HH:mm');
    return (
        <div style={{ lineHeight: '1.4' }}>
            <div className='table-font-size'>{dateStr} {timeStr}</div>
        </div>
    );
};

/**
 * Column definitions.
 *
 * All columns currently use filterType: 'list' (Filter Type 1 — checkbox list).
 * Filter Type 2 (DmsTextFilter — multi-condition text) code is preserved but not used.
 *
 * filterType  : 'list' → DmsListFilter
 *            // 'text' → DmsTextFilter (commented out — not currently active)
 * getValue    : formats the cell display value from a truck object
 * getOptions  : returns array of option strings to show in the checkbox list
 * matchesFilter: (truck, selectedValues) => bool — custom match; used when options differ from getValue
 */


// ─── Filter match helpers ────────────────────────────────────────────────────

/** Filter Type 1 — list/checkbox match */
const applyListFilter = (truck, col, filter) => {
    if (!filter || !filter.values || filter.values.length === 0) return true;

    if (col.matchesFilter) {
        return col.matchesFilter(truck, filter.values);
    }

    const value = col.filterValue
        ? col.filterValue(truck)
        : col.getValue(truck);

    return filter.values.includes(value);
};

/**
 * Filter Type 2 — multi-condition text match (kept for future use, currently unused)
 *
 * const matchTextCondition = (value, op, term) => { ... };
 * const applyTextFilter = (value, filter) => { ... };
 */

// ─── Component ──────────────────────────────────────────────────────────────

const SKELETON_ROWS = 8;

const DmsDashboardList = (props) => {
    const { searchStr, themeSelected, loading, selectedDate, latestTruckUpdate } = props;
    const { selectedWarehouse, isOnline, cu_id } = useSelector(getUserSelector);
    const user = useSelector(getUserSelector);
    const { userAccountDetails } = user;
    const { FirstName,LastName, ProfilePicture } = userAccountDetails;
    const { sessionToken } = useSelector(getAuthReducer);
    const {truckAuditData, DMSTruckAuditData} = useSelector(getDmsBucketSelector)
    const [tableData, setTableData] = useState([]);
    const [totalTableData, setTotalTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(0);

    // Add this
    const [editCell, setEditCell] = useState(null);
    const [srcData, setSrcData] = useState(null);

    // Column filter state: { colId: filterObject | null }
    const [columnFilters, setColumnFilters] = useState({});
    // Which column's filter popup is open (null = none)
    const [openFilterCol, setOpenFilterCol] = useState(null);

    const failureSubscriptionRef = useRef(null);
    const isOnlineRef = useRef(isOnline);
    const intervalRef = useRef(null);
    const dispatch = useDispatch();

    const [showTruckModal, setShowTruckModal] = useState(false);
    const [selectedTruck, setSelectedTruck] = useState(null);
    const [showTruckAuditModal, setShowTruckAuditModal] = useState(false);
    const [showTruckEditModal, setShowTruckEditModal] = useState(false);
    const [selectedControlNo, setSelectedControlNo] = useState('');
   
    const COLUMNS = [
    {
        id: 'dockType', name: 'Dock',
        description: 'Dock Type',
        filterType: 'list', // was: 'list'
        
        editable: false,
        
        getValue: t => t.Dock || '-',
        getOptions: t => [t.Dock || '-']
    },
    {
        id: 'door', name: 'Door#',
        description: 'Door #',
        filterType: 'list', // was: 'list'
        editable: false,
        editType: 'number',
        field: 'Door',

        getValue: t => t.Door?.toString() || '-',
        getOptions: t => [t.Door?.toString() || '-']
    },
    {
        id: 'truckSequence', name: 'Seq#',
        description: 'Truck Sequence Number',
        filterType: 'list', // was: 'list'
        editable: false,
        editType: 'number',
        field: 'TruckSequence',

        getValue: t => t.TruckSequence?.toString() || '-',
        getOptions: t => [t.TruckSequence?.toString() || '-']
    },
    // {
    //     id: 'control', name: 'Control#',
    //     description: 'Control #',
    //     filterType: 'list', // was: 'list'
    //     editable: false,
    //     editType: 'number',
    //     field: 'Control',

    //     getValue: t => t.Control?.toString() || '-',
    //     getOptions: t => [t.Control?.toString() || '-']
    // },
    //DMS Phase 2
    {
        id: 'truck',
        name: 'Truck #',
        description: 'Truck #',
        filterType: 'list',
        editable: false,

        // Used by filter popup
        getOptions: t => [t.Control || '-'],

        // Used by filtering logic
        filterValue: t => t.Control || '-',

        // Used by table rendering
        getValue: t => (
            <span
                className="truck-link"
                onClick={() => handleTruckClick(t.Control)}
                style={{
                    cursor: 'pointer',
                    textDecoration: 'underline'
                }}
            >
                {t.Control || '-'}
            </span>
        )
    },
    // {
    //     id: 'po', name: 'PO',
    //     description: 'PO',
    //     filterType: 'list', // was: 'list'
    //     editable: false,
    //     getValue: t => t.ScheduledPOs?.join(', ') || t.PO || '-',
    //     // Individual PO numbers shown as separate checkbox options
    //     getOptions: t => (t.ScheduledPOs?.length ? t.ScheduledPOs : (t.PO ? [t.PO] : ['-'])),
    //     // Match if ANY of the truck's POs is in the selected set
    //     matchesFilter: (t, selectedValues) => {
    //         const pos = t.ScheduledPOs?.length ? t.ScheduledPOs : (t.PO ? [t.PO] : []);
    //         if (pos.length === 0) return selectedValues.includes('-');
    //         return selectedValues.some(v => pos.includes(v));
    //     }
    // },
    {
        id: 'po',
        name: 'PO',
        description: 'PO',
        filterType: 'list',
        editable: false,

        getValue: t => {
            const pos = [
                ...(t.ScheduledPOs || []),
                ...(t.UnScheduledPOs || [])
            ];

            return pos.length ? pos.join(', ') : (t.PO || '-');
        },

        // Individual PO numbers shown as separate checkbox options
        getOptions: t => {
            const pos = [
                ...(t.ScheduledPOs || []),
                ...(t.UnScheduledPOs || [])
            ];

            return pos.length ? pos : (t.PO ? [t.PO] : ['-']);
        },

        // Match if ANY PO is selected
        matchesFilter: (t, selectedValues) => {
            const pos = [
                ...(t.ScheduledPOs || []),
                ...(t.UnScheduledPOs || [])
            ];

            if (pos.length === 0) {
                return selectedValues.includes('-');
            }

            return selectedValues.some(v => pos.includes(v));
        }
    },
    {
        id: 'supplier', name: 'Supplier',
        description: 'Supplier',
        editable: false,
        filterType: 'list', // Filter Type 2 ('text') would be used here when re-enabled
        getValue: t => t.Vendor || '-',
        getOptions: t => [t.Vendor || '-']
    },
    {
        id: 'delivery', name: 'Delivery',
        description: 'Delivery',
        editable: false,
        filterType: 'list', // Filter Type 2 ('text') would be used here when re-enabled
        getValue: t => t.Carrier || '-',
        getOptions: t => [t.Carrier || '-']
    },
    // {
    //     id: 'wh', name: 'WH Status',
    //     filterType: 'list', // Filter Type 2 ('text') would be used here when re-enabled
    //     getValue: () => 'NA',
    //     getOptions: () => ['NA']
    // },
    {
        id: 'appointmentTime', name: 'TruckApptTime',
        description: 'Appt Time',
        filterType: 'list', // was: 'list'
        editable: false,
        getValue: t => formatDateTime(t.ScheduledTime),
        getOptions: t => [formatDateTime(t.ScheduledTime)],
        render: t => renderDateTime(t.ScheduledTime)
    },
    {
        id: 'drvCheckIn', name: 'CheckInTime',
        description: 'Drv Check In',
        filterType: 'list', // Filter Type 2 ('text') would be used here when re-enabled
        actionType: "CHECK_IN",
        editable: true,
        editType: 'datetime',
        field: 'CheckInTime',

        getValue: t => formatDateTime(t.CheckInTime),
        getOptions: t => [formatDateTime(t.CheckInTime)],
        render: t => renderDateTime(t.CheckInTime)
    },
    {
        id: 'doorAssignedTime', name: 'DoorAssign',
        description: 'Door Assign Time',
        filterType: 'list', // was: 'list'
        editable: true,
        editType: 'datetime',
        field: 'InDoorTime',
        actionType: "DOOR_ASSIGNED",
        getValue: t => formatDateTime(t.InDoorTime),
        getOptions: t => [formatDateTime(t.InDoorTime)],
        render: t => renderDateTime(t.InDoorTime)
    },
    {
        id: 'finished', name: 'FinishedTime',
        description: 'Finished',
        filterType: 'list', // was: 'list'
        editable: true,
        editType: 'datetime',
        field: 'LumpEndTime',
        actionType: "FINISHED",
        getValue: t => formatDateTime(t.LumpEndTime),
        getOptions: t => [formatDateTime(t.LumpEndTime)],
        render: t => renderDateTime(t.LumpEndTime)
    },
    {
        id: 'completed', name: 'Completed',
        description: 'Completed',
        filterType: 'list', // Filter Type 2 ('text') would be used here when re-enabled
        editable: true,
        editType: 'datetime',
        field: 'OutDoorTime',
        actionType: "COMPLETED",
        getValue: t => formatDateTime(t.OutDoorTime),
        getOptions: t => [formatDateTime(t.OutDoorTime)],
        render: t => renderDateTime(t.OutDoorTime)
    },
    {
        id: 'load', name: 'LoadStatus',
        description: 'Load Status',
        filterType: 'list', // Filter Type 2 ('text') would be used here when re-enabled
        
        editable: false,
        editType: 'radio',
        field: 'TruckLoadStatus',

        options: [
            'Not Arrived',
            'Released',
            'Completed'
        ],
        getValue: t => t.TruckLoadStatus || '-',
        getOptions: t => [t.TruckLoadStatus || '-']
    },
    {
        id: 'notes',
        name: 'Notes',
        description: 'Notes',
        filterType: 'text',
        editable: true,
        editType: 'textarea',
        field: 'Notes',
        actionType: 'NOTES',

        getValue: t => {
            const notes = t.Notes?.toString() || '';

            if (!notes) return '-';

            return notes.length > 5
                ? `${notes.substring(0, 5)}...`
                : notes;
        },

        getOptions: t => [t.Notes?.toString() || '-']
    },
];
    
        useEffect(()=>{

            if(DMSTruckAuditData)
            {
                fetchDmsTrucks();

                dispatch({
                type: sagaActions.CLEAR_DMS_TRUCK_AUDIT,
                payload: {}
            });
                
            }
           
        },[DMSTruckAuditData]);

    useEffect(()=>{
        if (truckAuditData?.length > 0) 
        {
            setIsLoading(false);
            setShowTruckAuditModal(true);
        }
        else{
            if(showTruckModal === true){
               // showAlert(toast.TYPE.ERROR, "No Data available");  
                setShowTruckAuditModal(true);  
            }
            
        }
    },[truckAuditData])

    // ──Fetch truck audit data by control ──────────────────────────────────────────────
  
    const handleTruckClick = (controlNo) => {

        //clearDMSTruckAudit
        setSelectedControlNo(controlNo);
        setSelectedTruck(controlNo);
        setShowTruckModal(true);
        setIsLoading(true);

        dispatch({
            type: sagaActions.GET_DMS_TRUCK_AUDIT_BY_CONTROL,
            payload: {
                ControlNo: controlNo
            }
        });
        setIsLoading(false);
    };
    

    // ── Save truck edit data ──────────────────────────────────────────────
    const saveEdit = async () => {
        const { truck, column, value } = editCell;
        let updPayload= {
            "Control": truck.Control,
            "Action_Type": column.actionType,
            "Field_Date_Time": moment(value).format("YYYY-MM-DD HH:mm"),
            "Field_Text": value,
            "Created_By": FirstName+" "+LastName,
            "Created_By_Id": cu_id
            };

        //console.log(updPayload);
        try {
            
           dispatch({
                type: sagaActions.INSERT_DMS_TRUCK_AUDIT,
                payload: updPayload
            })
        } catch (error) {
            console.error("Error saving audit:", error);
        }
            
        const formattedDate = moment(selectedDate).format('YYYY-MM-DD');
       

        setEditCell(null);
        
    };
    // ── Outside Click Handler ──────────────────────────────────────────────
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (openFilterCol) {
                // Close if clicked outside any popup and outside any filter icon
                if (!e.target.closest('.dms-filter-popup') && !e.target.closest('.dms-col-filter-icon')) {
                    setOpenFilterCol(null);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openFilterCol]);

    // ── Data fetching ──────────────────────────────────────────────────────

    useEffect(() => {
        setTotalTableData(tableData.length);
    },[tableData]);
    const fetchDmsTrucks = async () => {
        try {
           // setIsLoading(true);
            const formattedDate = moment(selectedDate).format('YYYY-MM-DD');
            const response = await API.graphql(
                graphqlOperation(getDmsTrucks, {
                    IDWarehouse: selectedWarehouse.ID,
                    selectedDate: formattedDate
                })
            );
            const trucks = response?.data?.getDmsTrucks || [];
            //setTableData(trucks);
            const sortedTrucks = sortTruckData(trucks);
            setTableData(sortedTrucks);
           // console.log(sortedTrucks.length);
            setTotalTableData(sortedTrucks.length);
            //---
            //setTableData(sortTruckData(trucks));
           // console.log(tableData)
        } catch (error) {
            console.error('Error fetching DMS trucks:', error);
            setTableData([]);
        } finally {
           // setIsLoading(false);
        }
    };

    const getTruckPriority = (truck) => {
        const isCheckedIn = !!truck.CheckInTime;
        const isFinished = !!truck.OutDoorTime;

        // Green
        if (isCheckedIn && !isFinished) return 1;

        // Red
        if (!isCheckedIn) return 2;

        // Grey
        return 3;
    };

    const sortTruckData = (trucks = []) => {
        return [...trucks].sort((a, b) => {
            const priorityDiff = getTruckPriority(a) - getTruckPriority(b);

            // Same priority -> Appointment Time ascending
            if (priorityDiff === 0) {
                const dateA = a.ScheduledTime
                    ? new Date(a.ScheduledTime).getTime()
                    : Number.MAX_SAFE_INTEGER;

                const dateB = b.ScheduledTime
                    ? new Date(b.ScheduledTime).getTime()
                    : Number.MAX_SAFE_INTEGER;

                return dateA - dateB;
            }

            return priorityDiff;
        });
    };
    const getTruckRowClass = (truck) => {
        const isCheckedIn = !!truck.CheckInTime;
        const isFinished = !!truck.OutDoorTime;

        if (isCheckedIn && !isFinished) {
            return "dms-row-green";
        }

        if (!isCheckedIn) {
            return "dms-row-red";
        }

        return "dms-row-grey";
    };
    
    useEffect(() => {
        if (selectedWarehouse && selectedDate) {
            fetchDmsTrucks();
        }
    }, [selectedWarehouse, selectedDate]);

    // In-place update from subscription payload
    useEffect(() => {
        if (!latestTruckUpdate) return;
        const { operationType, ID } = latestTruckUpdate;
        console.log('🚚 DmsDashboardList: in-place update, operationType:', operationType, 'ID:', ID);
        // setTableData(prev => {
        //     if (operationType === 'DELETE') return prev.filter(t => t.ID !== ID);
        //     const exists = prev.some(t => t.ID === ID);
        //     return exists
        //         ? prev.map(t => t.ID === ID ? latestTruckUpdate : t)
        //         : [...prev, latestTruckUpdate];
        // });
        setTableData(prev => {
            let updatedData;

            if (operationType === 'DELETE') {
                updatedData = prev.filter(t => t.ID !== ID);
            } else {
                const exists = prev.some(t => t.ID === ID);

                updatedData = exists
                    ? prev.map(t => t.ID === ID ? latestTruckUpdate : t)
                    : [...prev, latestTruckUpdate];
            }

            return sortTruckData(updatedData);
        });
    }, [latestTruckUpdate]);

    // ── Failure subscription / reconnect ──────────────────────────────────

    const subscribeToFailures = () => {
        if (failureSubscriptionRef.current) failureSubscriptionRef.current.unsubscribe();
        failureSubscriptionRef.current = API.graphql({
            ...graphqlOperation(onRealTimeEventFailed),
            authMode: process.env.REACT_APP_APPSYNC_AUTHENTICATIONTYPE
        }).subscribe({
            next: ({ value }) => {
                console.log('🚀 ~ DMS Failure subscription ~ value:', value);
                if (selectedWarehouse && selectedDate) fetchDmsTrucks();
            },
            error: (error) => console.warn('DMS Failure subscription error:', error)
        });
    };

    const attemptReconnection = async () => {
        try {
            await API.graphql(graphqlOperation(_empty));
            if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
            if (selectedWarehouse && selectedDate) fetchDmsTrucks();
        } catch (error) {
            console.warn('DMS: Connection probe failed. Will retry.', error);
        }
    };

    useEffect(() => {
        if (isOnline && sessionToken && selectedWarehouse?.ID) subscribeToFailures();
        return () => {
            if (failureSubscriptionRef.current) {
                failureSubscriptionRef.current.unsubscribe();
                failureSubscriptionRef.current = null;
            }
        };
    }, [isOnline, sessionToken, selectedWarehouse?.ID]);

    useEffect(() => {
        if (isOnline && !isOnlineRef.current) {
            let attempts = 0;
            intervalRef.current = setInterval(() => {
                if (attempts < 3) { attempts++; attemptReconnection(); }
                else { clearInterval(intervalRef.current); intervalRef.current = null; }
            }, 30000);
        }
        isOnlineRef.current = isOnline;
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [isOnline]);

    // const saveDriverCheckIn = async () => {
    // try 
    // {
    //         await API.graphql(
    //             graphqlOperation(updateDriverCheckIn, {
    //                 ID: selectedTruck.ID,
    //                 CheckInTime: moment(checkInTime).toISOString()
    //             })
    //         );

    //         setShowCheckInModal(false);

    //         fetchDmsTrucks(); // refresh grid
    //     } catch (err) {
    //         console.error(err);
    //     }
    // };

    // ── Filter helpers ─────────────────────────────────────────────────────

    const getColumnOptions = (col) => {
        const optionSet = new Set();

        tableData.forEach(truck => {
            const opts = col.getOptions
                ? col.getOptions(truck)
                : [col.filterValue ? col.filterValue(truck) : col.getValue(truck)];

            opts.forEach(o => {
                if (o && o !== '-') optionSet.add(o);
            });
        });

        return Array.from(optionSet).sort();
    };
    const setFilter = (colId, filter) => {
        setColumnFilters(prev => ({ ...prev, [colId]: filter || null }));
        setOpenFilterCol(null);
    };

    const clearFilter = (colId) => {
        setColumnFilters(prev => { const next = { ...prev }; delete next[colId]; return next; });
        setOpenFilterCol(null);
    };

    const hasActiveFilter = (colId) => !!columnFilters[colId];

    // ── Derived filtered data ──────────────────────────────────────────────

    const filteredData = useMemo(() => {
        let data = [...tableData];

        // Global search filter
        if (searchStr && searchStr.trim()) {
            const terms = searchStr.toLowerCase().trim().split(/\s+/);
            data = data.filter(truck => {
                const text = [
                    truck.Dock || '',
                    truck.Door?.toString() || '',
                    truck.Control || '',
                    truck.Vendor || '',
                    truck.Carrier || '',
                    truck.TruckLoadStatus || '',
                    ...(truck.ScheduledPOs || [])
                ].join(' ').toLowerCase();

                return terms.every(t => text.includes(t));
            });
        }

        // Column filters
        Object.entries(columnFilters).forEach(([colId, filter]) => {
            if (!filter) return;
            const col = COLUMNS.find(c => c.id === colId);
            if (!col) return;

            data = data.filter(truck => applyListFilter(truck, col, filter));
          
        });
        data = sortTruckData(data);
        // // Sort by Appointment Time ASC
        // data.sort((a, b) => {
        //     const dateA = a.ScheduledTime ? new Date(a.ScheduledTime).getTime() : Number.MAX_SAFE_INTEGER;
        //     const dateB = b.ScheduledTime ? new Date(b.ScheduledTime).getTime() : Number.MAX_SAFE_INTEGER;

        //     return dateA - dateB;
        // });

        return data;
    }, [searchStr, tableData, columnFilters]);

    // ── Render ─────────────────────────────────────────────────────────────

    const showLoading = isLoading || loading;

    const renderHeaderCell = (col, index) => {
        const isOpen = openFilterCol === col.id;
        const isActive = hasActiveFilter(col.id);
        const alignRight = index >= COLUMNS.length - 3; // Align popup to the right for the last 3 columns

        return (
            <th scope="col"
                className={`col ${themeSelected} title-text-container`}
                id={col.id}
                key={col.id}
            >
                <div className="dms-th-wrapper">
                    <span className="table-font-size btn-title">{col.name}</span>
                    <i
                        className="fa fa-info-circle dms-info-icon"
                        title={col.description}
                    />
                    <div className="dms-filter-icon-wrapper">
                        <i
                            className={`fa fa-fw fa-filter dms-col-filter-icon ${
                                isActive ? 'active' : ''
                            }`}
                            title={isActive ? 'Filter active' : 'Filter'}
                            onClick={e => {
                                e.stopPropagation();
                                setOpenFilterCol(isOpen ? null : col.id);
                            }}
                        />

                        {isActive && <span className="dms-filter-dot" />}
                    </div>

                    {isOpen && (
                        <DmsListFilter
                            options={getColumnOptions(col)}
                            currentFilter={columnFilters[col.id] || null}
                            onApply={filter => setFilter(col.id, filter)}
                            onClear={() => clearFilter(col.id)}
                            alignRight={alignRight}
                        />
                    )}
                </div>
            </th>
        );
    };

    const renderSkeletonRows = () =>
        Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <tr key={i} className="dms-skeleton-row">
                {COLUMNS.map(col => (
                    <td key={col.id} className={`col p-3 ${themeSelected}`}>
                        <div className="dms-skeleton-cell" />
                    </td>
                ))}
            </tr>
        ));

    const renderCellContent = (truck, col) => {
        let content = col.render ? col.render(truck) : col.getValue(truck);

        if (col.id === 'dockType') {
            let indicatorClass = '';
            if (truck.underOneHour) indicatorClass = 'dms-time-indicator-under1';
            else if (truck.overOneHour) indicatorClass = 'dms-time-indicator-over1';
            else if (truck.overOnePointFiveHour) indicatorClass = 'dms-time-indicator-over1-5';

            if (indicatorClass) {
                return (
                    <div className="dms-time-indicator-container">
                        <div className={`dms-time-indicator-bar ${indicatorClass}`}></div>
                        <span>{content}</span>
                    </div>
                );
            }
        }

        // if (col.editable) {
        // return (
        //     <div
        //         className="dms-editable-cell"
        //         onClick={() => handleEdit(truck, col)}
        //     >
        //         {content}
        //     </div>
        // );
        // }
        if (col.editable) {

            // Notes column -> Popup
            if (col.editType === "textarea") {
                return (
                    <div
                        className="dms-editable-cell"
                        onClick={() => handleEdit(truck, col)}
                    >
                        {content}
                    </div>
                );
            }

            const isEditing =
                editCell &&
                editCell.truck.ID === truck.ID &&
                editCell.column.id === col.id;

            if (isEditing) {
                return (
                    <div className="d-flex align-items-center gap-1">

                        {col.editType === "datetime" ? (
                            <div style={{ width: "140px" }}>
                            <DatePicker
                                selected={editCell.value}
                                onChange={(date) =>
                                    setEditCell({
                                        ...editCell,
                                        value: date
                                    })
                                }
                                showTimeSelect
                                timeIntervals={15}
                                dateFormat="MM/dd/yyyy HH:mm"
                                timeFormat="HH:mm"
                                className="form-control"
                                popperPlacement="bottom-start"
                            />
                            </div>
                        ) : col.editType === "number" ? (
                            <input
                                type="number"
                                className="form-control"
                                value={editCell.value}
                                onChange={(e) =>
                                    setEditCell({
                                        ...editCell,
                                        value: e.target.value
                                    })
                                }
                            />
                        ) : (
                            <input
                                type="text"
                                className="form-control"
                                value={editCell.value}
                                onChange={(e) =>
                                    setEditCell({
                                        ...editCell,
                                        value: e.target.value
                                    })
                                }
                            />
                        )}

                        <button
                            className="btn btn-success btn-sm"
                            onClick={saveEdit}
                        >
                            ✓
                        </button>

                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => setEditCell(null)}
                        >
                            ✕
                        </button>

                    </div>
                );
            }

            return (
                <div
                    className="dms-editable-cell"
                    onClick={() => handleEdit(truck, col)}
                >
                    {content}
                </div>
            );
        }

        return content;
    };

    // const handleEdit = (truck, col) => {
    //     let value = '';

    //     if (col.editType === 'datetime') {
    //         value = truck[col.field]
    //             ? moment(truck[col.field]).format("YYYY-MM-DDTHH:mm")
    //             : moment().format("YYYY-MM-DDTHH:mm");
                
    //     } else {
    //         value = truck[col.field] || '';
    //     }

    //     setEditCell({
    //         truck,
    //         column: col,
    //         value
    //     });

    //     // Show popup only for textarea
    //     if (col.editType === 'textarea') {
    //         setShowTruckEditModal(true);
    //     }
    // };

    const handleEdit = (truck, col) => {
    let value = "";

    if (col.editType === "datetime") {
        value = truck[col.field]
            ? moment(truck[col.field], "YYYY-MM-DD HH:mm:ss").toDate()
            : new Date();
    } else {
        value = truck[col.field] || "";
    }

    setEditCell({
        truck,
        column: col,
        value
    });

    if (col.editType === "textarea") {
        setShowTruckEditModal(true);
    }
};
    
    const renderDataRows = () => {
        if (filteredData.length === 0) {
            return (
                <tr>
                    <td
                        colSpan={COLUMNS.length}
                        className={`text-center dms-dashboard-td ${themeSelected}`}
                        style={{ padding: '24px', fontSize: '13px', color: '#9e9e9e' }}
                    >
                        No records found
                    </td>
                </tr>
            );
        }
        return filteredData.map((truck, index) => (
            // <tr key={truck.ID || index} className={themeSelected}>
            <tr
                key={truck.ID || index}
                className={`${themeSelected} ${getTruckRowClass(truck)}`}
            >
                {COLUMNS.map(col => (
                    <td
                        key={col.id}
                        className={`col dms-dashboard-td ${themeSelected}`}
                        title={col.getValue(truck)}
                         style={{ fontSize: '10px'}}
                    >
                        {renderCellContent(truck, col)}
                    </td>
                ))}
            </tr>
        ));
    };

    const renderEditModalBody = () => {
        if (!editCell) return null;
        return (
            <div>
                <label htmlFor="editCellInput">
                    <span className="btn_title">{editCell.column.name}</span>
                </label>
                {editCell.column.editType === 'text' && (
                    <input
                        id="editCellInput"
                        type="text"
                        className="form-control"
                        value={editCell.value}
                        onChange={e =>
                            setEditCell({
                                ...editCell,
                                value: e.target.value
                            })
                        }
                    />
                )}
                {editCell.column.editType === 'textarea' && (
                    <textarea
                        id="editCellInput"
                        className="form-control"
                        placeholder="Enter your notes"
                        rows={6}
                        value={editCell.value || ''}
                        onChange={e =>
                            setEditCell({
                                ...editCell,
                                value: e.target.value
                            })
                        }
                    />
                )}
                {editCell.column.editType === 'number' && (
                    <input
                        id="editCellInput"
                        type="number"
                        className="form-control"
                        value={editCell.value}
                        onChange={e =>
                            setEditCell({
                                ...editCell,
                                value: e.target.value
                            })
                        }
                    />
                )}
                {editCell.column.editType === 'datetime' && (
                    <input
                        id="editCellInput"
                        type="datetime"
                        className="form-control"
                        value={moment(editCell.value).format("MM/DD/YYYY HH:mm")}
                        onChange={e =>
                            setEditCell({
                                ...editCell,
                                value: e.target.value
                            })
                        }
                    />
                )}
            </div>
        );
    };

    return (
        <>
        <div className="dms-table-wrapper">
            <table
                className={`table table-responsive table-hover ${themeSelected}`}
                id="dms-truck-table">
                <thead>
                    <tr className={`text-start align-middle ${themeSelected} truck-list-title`}>
                        {COLUMNS.map((col, index) => renderHeaderCell(col, index))}
                    </tr>
                </thead>
                <tbody>
                    {/* {showLoading ? renderSkeletonRows() : renderDataRows()} */}
                    {renderDataRows()}
                </tbody>
            </table>
            <div className='dms-total-count'>Total Loads: {totalTableData}</div>
        </div>

        {editCell && editCell.column.editType === "textarea" && (
            <Popup
                showModal={true}
                closepopup={() => setEditCell(null)}
                title={editCell.column.name}
                showClose={true}
                showHeader={true}
                showfooter={true}
                modalSize="md"
                footerBtn={{
                    btn1: { show: true, text: "Cancel" },
                    btn2: { show: true, text: "Save" }
                }}
                popupBtnHandler={(text) => {
                    if (text === "Save") {
                        saveEdit();
                    } else {
                        setEditCell(null);
                    }
                }}
                bodyText={renderEditModalBody()}
            />
        )}

        {showTruckModal && (
            <Popup
            showModal={showTruckAuditModal}
            closepopup={() => setShowTruckAuditModal(false)}
            title={`Truck Audit - ${selectedControlNo}`}
            showClose={true}
            showfooter={false}
            modalSize="xl"
            bodyText={
                <div className="dms-table-wrapper">
                    {truckAuditData && truckAuditData.length > 0 ? (
                        <table className={`table table-responsive table-hover ${themeSelected}`}>
                            <thead>
                                <tr>
                                    <th className={`text-start align-middle ${themeSelected} truck-list-title`}>
                                        Action Type
                                    </th>
                                    <th className={`text-start align-middle ${themeSelected} truck-list-title`}>
                                        Action Details
                                    </th>
                                    <th className={`text-start align-middle ${themeSelected} truck-list-title`}>
                                        Updated By
                                    </th>
                                    <th className={`text-start align-middle ${themeSelected} truck-list-title`}>
                                        Updated Time
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {truckAuditData.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.Action_Type.replace("_", " ")}</td>
                                        <td>
                                           {item.Action_Type === "NOTES"
                                                    ? item.Field_Text
                                                    : item.Field_Date_Time
                                                    ? moment(item.Field_Date_Time)
                                                        .utc()
                                                        .format("MM/DD/YYYY HH:mm:ss")
                                                    : "-"
                                                }
                                            
                                        </td>
                                        <td>{item.CreatedBy}</td>
                                        <td>
                                            {item.Created_Date
                                                ? moment(item.Created_Date)
                                                    .utc()
                                                    .format("MM/DD/YYYY HH:mm:ss")
                                                : "-"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="text-center py-4">
                            <h5 className="text-muted mb-0">No Data Available</h5>
                        </div>
                    )}
                </div>
            }
        />
        )}
    </>
    );
};

export default DmsDashboardList;